import { useEffect, useState } from "react";
import { contarPendentes, definirModoOnibus, modoOnibusLigado } from "../offline/fila.js";
import { baixarPacote, esvaziarFila } from "../offline/sincronizar.js";
import { Icone } from "./Icone.js";
import { Interruptor } from "./Controles.js";
import { IconeSemSinal } from "./icones.js";

/**
 * A faixa que diz o estado da conexão e da fila.
 *
 * Existe porque **o iOS não tem Background Sync**: a sincronização acontece
 * quando o app é aberto com sinal, não sozinha no bolso. Um contador de
 * pendentes visível é o que impede isso de virar surpresa três dias depois.
 */
export function EstadoOffline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pendentes, setPendentes] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<string | null>(null);

  async function sincronizar() {
    if (!navigator.onLine) return;
    setSincronizando(true);
    try {
      const r = await esvaziarFila();
      setPendentes(r.restantes);
      if (r.enviadas > 0 || r.jaEstavam > 0) {
        setRecado(
          `${r.enviadas} ${r.enviadas === 1 ? "resposta enviada" : "respostas enviadas"}${r.jaEstavam > 0 ? ` · ${r.jaEstavam} já tinham chegado` : ""}.`,
        );
      }
      // A recusa tem faixa própria e não some junto com o recado: ela é perda de
      // resposta, e sumir em silêncio é o que o ADR 027 veio corrigir.
      setRecusa(
        r.recusadas > 0
          ? `${r.recusadas} ${r.recusadas === 1 ? "resposta não foi aceita" : "respostas não foram aceitas"}. ${r.motivoRecusa ?? ""}`.trim()
          : null,
      );
    } finally {
      setSincronizando(false);
    }
  }

  useEffect(() => {
    void contarPendentes().then(setPendentes);
    // Ao abrir o app e ao voltar o sinal — os dois momentos em que a fila pode
    // andar sem o usuário pedir.
    void sincronizar();

    const aoVoltar = () => {
      setOnline(true);
      void sincronizar();
    };
    const aoCair = () => setOnline(false);
    window.addEventListener("online", aoVoltar);
    window.addEventListener("offline", aoCair);
    return () => {
      window.removeEventListener("online", aoVoltar);
      window.removeEventListener("offline", aoCair);
    };
  }, []);

  if (online && pendentes === 0 && !recado && !recusa) return null;

  return (
    <div className={online ? "faixa-offline faixa-offline--online" : "faixa-offline"} role="status" aria-live="polite">
      {/* A nuvem cortada é o que se lê antes da frase: a faixa aparece no
          meio de uma sessão, e a silhueta chega antes da leitura. */}
      {!online && (
        <span className="faixa-offline__aviso">
          <Icone glifo={IconeSemSinal} papel="estado" tamanho="p" />
          Sem conexão — suas respostas ficam guardadas no aparelho.
        </span>
      )}
      {pendentes > 0 && (
        <span>
          <span className="dado">{pendentes}</span> {pendentes === 1 ? "resposta" : "respostas"} esperando enviar.
        </span>
      )}
      {online && pendentes > 0 && (
        <button type="button" className="botao botao--texto" onClick={() => void sincronizar()} disabled={sincronizando}>
          {sincronizando ? "Enviando…" : "Enviar agora"}
        </button>
      )}
      {recado && pendentes === 0 && <span>{recado}</span>}
      {recusa && (
        <span className="faixa-offline__aviso">
          {recusa}{" "}
          <button type="button" className="botao botao--texto" onClick={() => setRecusa(null)}>
            Entendi
          </button>
        </span>
      )}
    </div>
  );
}

/**
 * O interruptor do modo ônibus, para os Ajustes.
 *
 * Desligado por padrão. Ligado, os gabaritos da sessão e das revisões do dia
 * ficam no aparelho — e a tela diz isso sem eufemismo, porque é uma troca real
 * em relação ao que a F1 garantia.
 */
export function ModoOnibus() {
  const [ligado, setLigado] = useState(modoOnibusLigado);
  const [baixando, setBaixando] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);

  async function alternar() {
    const novo = !ligado;
    definirModoOnibus(novo);
    setLigado(novo);
    setRecado(null);
    if (!novo) {
      setRecado("Gabaritos apagados do aparelho.");
      return;
    }
    setBaixando(true);
    try {
      const n = await baixarPacote();
      setRecado(
        n === 0
          ? "Nada para baixar ainda: monte uma sessão e volte aqui."
          : `${n} ${n === 1 ? "item baixado" : "itens baixados"} para uso sem sinal.`,
      );
    } catch {
      setRecado("Não deu para baixar agora. Tente com sinal.");
    } finally {
      setBaixando(false);
    }
  }

  return (
    <div className="linha-ajuste">
      <Interruptor
        rotulo={baixando ? "Modo ônibus — baixando…" : "Modo ônibus"}
        ajuda={
          <>
            Baixa os itens da sessão e das revisões de hoje para responder sem sinal. Enquanto ligado,{" "}
            <strong>o gabarito desses itens fica guardado no aparelho</strong> — desligado, ele nunca desce.
            {recado && (
              <span className="interruptor__recado" aria-live="polite">
                {" "}
                {recado}
              </span>
            )}
          </>
        }
        ligado={ligado}
        desabilitado={baixando}
        aoMudar={() => void alternar()}
      />
    </div>
  );
}
